'use client';

import { useEffect, useMemo, useState } from 'react';

type ScrollSpyOptions = {
    ids: string[];
    threshold?: number | number[];
    rootMargin?: string;
    updateHash?: boolean;
    preferTopmost?: boolean;
};

export function useScrollSpy({
    ids,
    threshold = 0.1,
    rootMargin = '-80px 0px -70% 0px',
    updateHash = false,
    preferTopmost = true,
}: ScrollSpyOptions) {
    const [activeId, setActiveId] = useState<string>(ids[0] ?? '');
    const visible = useMemo(() => new Map<string, IntersectionObserverEntry>(), []);

    useEffect(() => {
        if (typeof window === 'undefined') return;
        if (!ids.length) return;

        const elements = ids.map((id) => document.getElementById(id)).filter(Boolean) as HTMLElement[];

        if (!elements.length) return;

        const pickActive = () => {
            const entries = Array.from(visible.values()).filter((e) => e.isIntersecting);

            if (!entries.length) {
                setActiveId('');
                return;
            }

            let next: IntersectionObserverEntry;

            if (preferTopmost) {
                next = entries.reduce((a, b) => (a.boundingClientRect.top < b.boundingClientRect.top ? a : b));
            } else {
                next = entries.reduce((a, b) => (a.intersectionRatio > b.intersectionRatio ? a : b));
            }

            const id = (next.target as HTMLElement).id;
            setActiveId((prev) => (prev === id ? prev : id));

            if (updateHash) {
                history.replaceState(null, '', `#${id}`);
            }
        };

        const observer = new IntersectionObserver(
            (obsEntries) => {
                for (const entry of obsEntries) {
                    const id = (entry.target as HTMLElement).id;
                    visible.set(id, entry);
                }
                pickActive();
            },
            { threshold, rootMargin },
        );

        for (const el of elements) observer.observe(el);

        return () => observer.disconnect();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ids.join('|'), threshold, rootMargin, updateHash, preferTopmost]);

    return activeId;
}
